import React from 'react';
import { MenuItem, Select, TableBody, TableCell, TableRow, TextField } from '@material-ui/core';
import { FieldPath, FieldValues, UseFormReturn, Controller } from 'react-hook-form';
import { ClinicProduct } from '../../../../apis/clinic-products/types';
import PriceEditor, { PriceEditorProps } from '../../../FormEditors/PriceEditor';
import PriceInput from '../../../Shared/Form/PriceInput/PriceInput';
import CheckboxEditor from '../../../FormEditors/CheckboxEditor';

interface ClinicProductsTableBodyProps {
  form: UseFormReturn<ClinicProduct[]>;
}

const ClinicProductsTableBody = ({ form }: ClinicProductsTableBodyProps) => {
  const objectifiedProducts = form.watch();
  const products = Object.values(objectifiedProducts);

  return (
    <TableBody>
      {products.map((value, index) => (
        <TableRow key={value._id}>
          <TableCell component="th" scope="row">
            {value.virtual_medicine_name}
          </TableCell>
          <PriceEditorCell control={form.control} name={`${index}.clinic_price`} />
          <PriceEditorCell control={form.control} name={`${index}.clinic_fee`} />
          <HealtheraFeeCell index={index} form={form} />
          <PriceEditorCell control={form.control} name={`${index}.pharmacy_fee`} />
          <TextEditorCell form={form} name={`${index}.course_length`} />
          <TextEditorCell form={form} name={`${index}.promotion`} />
          <TextEditorCell form={form} name={`${index}.price_per_usage`} />
          <SelectCell
            form={form}
            name={`${index}.fixed_cycle`}
            options={[
              { value: true, label: 'TRUE' },
              { value: false, label: 'FALSE' },
              { value: '', label: 'NULL' }
            ]}
          />
          <TextEditorCell form={form} name={`${index}.cycle_length`} />
          <TableCell>
            <CheckboxEditor control={form.control} name={`${index}.is_active`} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
};

function PriceEditorCell<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
  props: Readonly<PriceEditorProps<TFieldValues, TName>>
) {
  return (
    <TableCell align="right" padding="checkbox">
      <PriceEditor {...props} />
    </TableCell>
  );
}

interface HealtheraFeeCellProps extends ClinicProductsTableBodyProps {
  index: number;
}

function HealtheraFeeCell({ index, form }: HealtheraFeeCellProps) {
  const { clinic_price, clinic_fee, pharmacy_fee } = form.watch(`${index}`);

  return (
    <TableCell align="right" padding="checkbox">
      <PriceInput value={(clinic_price - clinic_fee - pharmacy_fee).toFixed(2)} disabled />
    </TableCell>
  );
}

interface TextEditorCellProps<TFieldValues extends FieldValues> {
  form: UseFormReturn<TFieldValues>;
  name: FieldPath<TFieldValues>;
}

function TextEditorCell<TFieldValues extends FieldValues>({ form, name }: TextEditorCellProps<TFieldValues>) {
  return (
    <TableCell padding="checkbox">
      <Controller
        name={name}
        control={form.control}
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField
              value={field.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value === '' ? null : e.target.value)}
              multiline
              error={Boolean(error?.message)}
              helperText={error?.message}
            />
          );
        }}
      />
    </TableCell>
  );
}

interface SelectCellProps<TFieldValues extends FieldValues> {
  form: UseFormReturn<TFieldValues>;
  name: FieldPath<TFieldValues>;
  options: { value: any; label: string }[];
}

function SelectCell<TFieldValues extends FieldValues>({ form, name, options }: SelectCellProps<TFieldValues>) {
  return (
    <TableCell padding="checkbox">
      <Controller
        name={name}
        control={form.control}
        render={({ field, fieldState }) => (
          <>
            <Select
              value={[undefined, null].includes(field.value) ? '' : field.value}
              onChange={e => field.onChange(e.target.value === '' ? null : e.target.value)}
              error={Boolean(fieldState.error)}
            >
              {options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {fieldState.error && <span>{fieldState.error.message}</span>}
          </>
        )}
      />
    </TableCell>
  );
}

export default ClinicProductsTableBody;
